import { AxiosService } from '../../axios/axios.service.ts';
import { tAutoRespondBulkResponse, tAutoRespondRequest, tAutoRespondResponse } from './AI.types.ts';
import { setSlbChatMapping, clearSlbChatMap } from '../../pages/Pipe/slbChatMap.ts';

// Keep UUID request targets below 2 KiB so the BFF's 8 KiB read buffer also
// has room for session and forwarded headers.
const AUTO_RESPOND_BATCH_SIZE = 50;

export const autoRespondService = {
  getState: async function (
    workspaceId: string,
    chatId: string,
  ): Promise<tAutoRespondResponse> {
    return AxiosService.get<tAutoRespondResponse>(
      `/ai/workspaces/${workspaceId}/chats/${chatId}/auto-respond`,
      { isLegacy: false },
    );
  },

  getBulkState: async function (
    workspaceId: string,
    leadIds: string[],
  ): Promise<tAutoRespondBulkResponse> {
    const normalized: tAutoRespondBulkResponse = { states: [] };
    const batchCount = Math.max(1, Math.ceil(leadIds.length / AUTO_RESPOND_BATCH_SIZE));
    for (let batch = 0; batch < batchCount; batch += 1) {
      const ids = leadIds.slice(
        batch * AUTO_RESPOND_BATCH_SIZE,
        (batch + 1) * AUTO_RESPOND_BATCH_SIZE,
      );
      const response = await AxiosService.get<
        tAutoRespondBulkResponse | tAutoRespondBulkResponse['states']
      >(
        `ai/workspaces/${workspaceId}/auto-respond/bulk?leadIds=${ids.join(',')}`,
        { isBFFService: true },
      );
      // BFF wraps the states; chatbot-service can return the array directly.
      normalized.states.push(...(Array.isArray(response) ? response : response.states));
    }
    // Populate the SLB chat map so the pipe kanban can open the correct chat
    clearSlbChatMap();
    for (const s of normalized.states) {
      if (s.enabled && s.chatId && s.channelType) {
        setSlbChatMapping(s.leadId, { chatId: s.chatId, channelType: s.channelType });
      }
    }
    return normalized;
  },

  toggle: async function (
    chatId: string,
    request: tAutoRespondRequest,
  ): Promise<tAutoRespondResponse> {
    try {
      const response = await AxiosService.patch<
        tAutoRespondRequest,
        tAutoRespondResponse
      >(`/ai/workspaces/${request.workspaceId}/chats/${chatId}/auto-respond`, request, {
        isLegacy: false,
      });

      return Promise.resolve(response);
    } catch (error) {
      console.error('Error toggling auto-respond:', error);
      return Promise.reject(error);
    }
  },
};
